import { BindingEngine, computedFrom } from "aurelia-binding";
import { autoinject, transient } from "aurelia-dependency-injection";
import { EventAggregator } from "aurelia-event-aggregator";
import { I18N } from "aurelia-i18n";
import { Router } from "aurelia-router";
import { Claims } from "../../../../../../constants/authentication/claims";
import { TitleWithCountHelper } from "../../../../../../helpers/title-with-count-helper";
import { UrlHelper } from "../../../../../../helpers/url-helper";
import { StepModel } from "../../../../../../models/basics/basics.models";
import { VesselDetailModel } from "../../../../../../models/vessels/vessel.models";
import { AutoScreenStepEnum, VetClientModel } from "../../../../../../models/vetting/vetting.models";
import { UserClaimsService } from "../../../../../../services/authentication/user-claims.service";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { NotificationClientService } from "../../../../../../services/basics/notification-client.service";
import { SignalClientService } from "@rightship-tech/common-ui/services/basic/signal-client.service";
import { ValidationsClientService } from "../../../../../../services/validations/validations-client.service";
import { ManageVesselService } from "../../../../../../services/vessels/manage-vessel.service";
import { EntityComments } from "../../../../common/entity-notes/entity-notes";
import { EmailHistory } from "../../../common-steps/email-history/email-history";
import { VetDocuments } from "../../../vetting/steps/documents/vet-documents";
import { VetRulesRun } from "../../../vetting/steps/rules-run/vet-rules-run";
import { VetCriteria } from "../../../vetting/steps/vet-criteria/vet-criteria";
import { VetHistoryTrail } from "../../../vetting/steps/vet-history-trail/vet-history-trail";
import { TradeCheckOverview } from "./tabs/overview/trade-check-overview";
import { BaseAutoScreeningTemplate } from "../base-auto-screening";
import { RightShipQiRouterConstants } from "../../../../../../configuration/rightshipqi-router.constants";
import { ConfigService } from "@rightship-tech/common-ui/services/basic/config.service";
import { useView } from "aurelia-framework";

@autoinject()
@transient()
@useView('views/pages/modules/auto-screening/templates/trade-check/trade-check.html')
export class TradeCheckTemplate extends BaseAutoScreeningTemplate {
    
    public showAIExperiment: boolean = false;

    public forceLoadVesselDetails: boolean = false;

    public canProcessSanctionedVet: boolean = false;

    public canProcessVetUnknownPort: boolean = false;

    public canViewVetDocuments: boolean = false;

    constructor(protected dialogClientService:DialogClientService, protected userClaimsService:UserClaimsService, 
            router:Router, i18N:I18N, eventAggregator:EventAggregator, validationsClientService:ValidationsClientService,
            bindingEngine:BindingEngine, notificationService:NotificationClientService, signalClientService: SignalClientService,
            manageVesselService:ManageVesselService, vetRulesRun: VetRulesRun, private vetHistoryTrail: VetHistoryTrail,
            private emailHistory: EmailHistory,  private tradeCheckOverview: TradeCheckOverview,
            private vetDocuments: VetDocuments, private entityComments: EntityComments, vetCriteria: VetCriteria,
            private configService: ConfigService){
            super(router, bindingEngine, i18N, notificationService, eventAggregator, 
                validationsClientService, signalClientService, manageVesselService, vetCriteria, vetRulesRun, userClaimsService);
        //Claims
        this.showAIExperiment = userClaimsService.hasPermission(Claims.ShowAIExperiment);
        this.canViewVettingEmailHistory = userClaimsService.hasPermission(Claims.CanViewVettingEmailHistory);
        this.canProcessSanctionedVet = this.userClaimsService.hasPermission(Claims.CanProcessSanctionedVet);
        this.canProcessVetUnknownPort = this.userClaimsService.hasPermission(Claims.CanProcessVetUnknownPort);
        this.canViewVetDocuments = this.userClaimsService.hasPermission(Claims.CanViewVetDocuments);
    }

    async activate(param: { item: VetClientModel; vessel: VesselDetailModel; }) {
        super.activate(param);
        const forceLoad = UrlHelper.getParameterValueByKey("forceLoad");
        const value = forceLoad == "true" ? true : false;
        this.forceLoadVesselDetails = value;
    }

    protected getCriteriaTitle(){
        return "Criteria";
    }

    onItemSet()
    {
        if(this.tradeCheckOverview)
            this.tradeCheckOverview.item = this.item;
    }

    navigateToFullVet() {
        this.router.navigateToRoute(RightShipQiRouterConstants.RouteNames.Vet, { id: this.item.fullVetId });
    }
    
    @computedFrom("vessel.internationalMaritimeOrganizationNumber")
    get aiExpirimentLink() {

        if (this.vessel && this.vessel.internationalMaritimeOrganizationNumber && this.showAIExperiment) {
            return this.configService.config.urls.redMarbleUrl + '/VesselInfo?imo=' + this.vessel.internationalMaritimeOrganizationNumber
        }

        return null;
    }

    @computedFrom("isSanctionedCountry", "canProcessSanctionedVet", "isUnknownCountry", "canProcessVetUnknownPort")
    get canProcessVet() {
        return (!this.isSanctionedCountry || this.canProcessSanctionedVet) &&
            (!this.isUnknownCountry || this.canProcessVetUnknownPort)
    }

    @computedFrom("item", "item.portCountryOfLoadSanctioned", "item.portCountryOfDischargeSanctioned")
    get isSanctionedCountry(): boolean {
        return this.item.portCountryOfLoadSanctioned || this.item.portCountryOfDischargeSanctioned;
    }

    @computedFrom("item", "item.portCountryOfDischargeUnknown", "item.portCountryOfLoadUnknown")
    get isUnknownCountry(): boolean {
        return this.item.portCountryOfDischargeUnknown || this.item.portCountryOfLoadUnknown;
    }

    protected getSteps(): StepModel[] {
        return [
            {
                lazyLoad: true,
                title: "Overview",
                step: AutoScreenStepEnum.AutoScreenOverview,
                vm: this.tradeCheckOverview,
                show: true,
                onClick: () => {
                    this.tradeCheckOverview.vessel = this.vessel;
                }
            },
            ...this.getScreeningCommonSteps(),
            {
                lazyLoad: true,
                title: () => TitleWithCountHelper.getTitle(this.i18N.tr("vet-documents-text"), this.item.documentsCount),
                step: AutoScreenStepEnum.VetDocuments,
                vm: this.vetDocuments,
                show: this.canViewVetDocuments
            },
            {
                lazyLoad: true,
                title: () => TitleWithCountHelper.getTitle(this.i18N.tr("vet-private-notes-text"), this.item.privateNotesCount),
                step: AutoScreenStepEnum.VetComments,
                vm: this.entityComments,
                show: this.item.vetId > 0 && this.userClaimsService.hasPermission(Claims.CanViewVetNotes),
                getModel: () => ({
                    canSubmit: this.canProcessVet,
                    entityTypeId: this.item.entityType,
                    entityId: this.item.vetId,
                    item: this.item
                })
            },
            {
                lazyLoad: true,
                title: "Email History",
                step: AutoScreenStepEnum.EmailHistory,
                vm: this.emailHistory,
                getModel: () => {
                    return {
                        item: this.item
                    }
                },
                show: () => this.canViewVettingEmailHistory && !!this.item
            },
            {
                lazyLoad: true,
                title: this.i18N.tr("history-trail-text"),
                step: AutoScreenStepEnum.HistoryTrail,
                vm: this.vetHistoryTrail,
                show: this.item.vetId > 0 && this.userClaimsService.hasPermission(Claims.CanViewHistoryTrail)
            }
        ]
    }
}